import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { Drawer, TextInput } from '@mantine/core';
import {
    useDebouncedState,
    useDebouncedValue,
    useDisclosure
} from '@mantine/hooks';
import { DataTable } from 'mantine-datatable';
// icons
import { ListFilter } from 'lucide-react';
// components
import { ResponsiveButton } from '@newo/ui-kit';
import { IConversationsUserPersona } from '@newo/api/gateway';
import { getFiltersCount } from '@newo/toolkit/bff';
import PageContainer from '@/components/Layout/components/PageContainer';
import Filters from '@/pages/Contacts/components/Filters';
import Header from '@/components/Header';
// utils
import useContactsStore from '@/pages/Contacts/Contacts.store.ts';
import { useColumns } from '@/pages/Contacts/Contacts.hooks.tsx';
import { useContactsQuery } from '@/services/bff/bff.queries.ts';
import { PAGE_SIZES } from '@/pages/Contacts/Contacts.utils.ts';
import { getDateRange } from '@/utils/filters';

const START_PAGE = 1;
const DEBOUNCE_TIMEOUT = 200;
const DEFAULT_TOTAL = 0;

const Contacts = () => {
    const [query, setQuery] = useDebouncedState('', DEBOUNCE_TIMEOUT);
    const [isOpened, handlers] = useDisclosure();
    const { filters, dateRange, pagination, setPagination } = useContactsStore(
        useShallow((state) => ({
            filters: state.filters,
            dateRange: state.dateRange,
            pagination: state.pagination,
            setPagination: state.setPagination
        }))
    );
    const [debounced] = useDebouncedValue(filters, DEBOUNCE_TIMEOUT);

    const _dateRange = useMemo(() => {
        if (dateRange) {
            return getDateRange(dateRange);
        }
    }, [dateRange]);

    const { data, isLoading } = useContactsQuery({
        ...debounced,
        ..._dateRange,
        ...pagination,
        hide_not_found: true,
        skip_without_user_messages: true,
        query: query || undefined
    });

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.currentTarget.value);
    };

    const columns = useColumns();

    const contacts = useMemo(() => data?.items ?? [], [data]);

    const total = data?.metadata.total ?? DEFAULT_TOTAL;
    const counter = getFiltersCount({ ...filters, dateRange }, [
        'page',
        'per',
        'hide_not_found',
        'skip_without_user_messages'
    ]);

    const handleDrawerClose = () => {
        handlers.close();
    };

    const handleFiltersToggle = () => {
        handlers.toggle();
    };

    const handlePageChange = (newPage: number) => {
        setPagination({ page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPagination({ per: newPageSize, page: START_PAGE });
    };

    return (
        <>
            <Helmet>
                <title>NEWO - Contacts</title>
            </Helmet>
            <PageContainer>
                <Header title="Contacts" />
                <div className="flex justify-between items-end gap-2 mb-4">
                    <TextInput
                        className="flex-1 md:max-w-lg mr-auto"
                        label="Search"
                        placeholder="Search by phone number, name"
                        size="xs"
                        onChange={handleSearch}
                    />
                    <ResponsiveButton
                        counter={counter}
                        icon={<ListFilter />}
                        onClick={handleFiltersToggle}
                    >
                        Filters
                    </ResponsiveButton>
                </div>
                <DataTable
                    pinFirstColumn
                    columns={columns}
                    fetching={isLoading}
                    height="calc(100vh - var(--header-height) - var(--navbar-mobile-height))"
                    idAccessor="id"
                    loaderBackgroundBlur={1}
                    loaderColor="blue"
                    page={pagination.page}
                    records={contacts}
                    recordsPerPage={pagination.per}
                    recordsPerPageOptions={PAGE_SIZES}
                    rowClassName={(contact: IConversationsUserPersona) => {
                        if (contact.session_is_active) {
                            return 'bg-green-0';
                        }
                        return '';
                    }}
                    scrollAreaProps={{
                        scrollbarSize: 10,
                        type: 'always'
                    }}
                    totalRecords={total}
                    verticalAlign="top"
                    onPageChange={handlePageChange}
                    onRecordsPerPageChange={handlePageSizeChange}
                />
            </PageContainer>
            <Drawer
                opened={isOpened}
                position="right"
                title="Filters"
                onClose={handleDrawerClose}
            >
                <Filters total={total} />
            </Drawer>
        </>
    );
};

export default Contacts;
